import type { OrderItem, OrderStatus, OrderSummary } from "./types";

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  CRIADO: "Criado",
  EM_COMPRA: "Em compra",
  EM_PRODUCAO: "Em produção",
  EM_EXPEDICAO: "Expedição",
  EM_ROTA: "Em entrega",
  ENTREGUE: "Entregue",
  CANCELADO: "Cancelado",
};

export const ORDER_STATUS_ORDER: OrderStatus[] = [
  "CRIADO",
  "EM_COMPRA",
  "EM_PRODUCAO",
  "EM_EXPEDICAO",
  "EM_ROTA",
  "ENTREGUE",
  "CANCELADO",
];

export const ORDER_STATUS_COLORS: Record<OrderStatus, { dot: string; bg: string; text: string; border: string }> = {
  CRIADO: { dot: "bg-info", bg: "bg-info/10", text: "text-info", border: "border-info/30" },
  EM_COMPRA: { dot: "bg-amber-500", bg: "bg-amber-500/10", text: "text-amber-700", border: "border-amber-500/30" },
  EM_PRODUCAO: { dot: "bg-warning", bg: "bg-warning/10", text: "text-warning", border: "border-warning/30" },
  EM_EXPEDICAO: { dot: "bg-teal-500", bg: "bg-teal-500/10", text: "text-teal-700", border: "border-teal-500/30" },
  EM_ROTA: { dot: "bg-primary", bg: "bg-primary/10", text: "text-primary", border: "border-primary/30" },
  ENTREGUE: { dot: "bg-success", bg: "bg-success/10", text: "text-success", border: "border-success/30" },
  CANCELADO: { dot: "bg-muted-foreground", bg: "bg-muted", text: "text-muted-foreground", border: "border-border" },
};

const STATUS_FLOW: OrderStatus[] = ["CRIADO", "EM_COMPRA", "EM_PRODUCAO", "EM_EXPEDICAO", "EM_ROTA", "ENTREGUE"];

export function nextOrderStatus(status: OrderStatus): OrderStatus | null {
  const index = STATUS_FLOW.indexOf(status);
  if (index === -1 || index === STATUS_FLOW.length - 1) return null;
  return STATUS_FLOW[index + 1];
}
export function previousOrderStatus(status: OrderStatus): OrderStatus | null {
  const index = STATUS_FLOW.indexOf(status);
  if (index <= 0) return null;
  return STATUS_FLOW[index - 1];
}

export function calculateOrderTotal(items: OrderItem[]): number | undefined {
  if (items.some((item) => item.pricePerUnit === undefined)) return undefined;
  return +items.reduce((total, item) => total + (item.pricePerUnit ?? 0) * item.quantity, 0).toFixed(2);
}

export function timeSince(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `há ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  return `há ${days}d`;
}

export function orderReference(order: Pick<OrderSummary, "id" | "number">): string {
  // Usa o número real do pedido (sequência do banco), o mesmo exibido no Relatório de Produção.
  // O hash do UUID é só fallback legado para resumos que porventura não tragam `number`.
  if (order.number != null) return `Pedido #${order.number}`;
  let hash = 0;
  for (const char of order.id) {
    hash = (hash * 31 + char.charCodeAt(0)) % 9000;
  }
  return `Pedido #${hash + 1000}`;
}

/** Quantos nomes de item mostrar antes de resumir em "+N". `itemPreview` traz o pedido inteiro
 * (a busca depende disso); o corte para exibição é responsabilidade do FE. */
export const ORDER_ITEM_PREVIEW_LIMIT = 2;

export function orderTitle(order: Pick<OrderSummary, "clientName" | "itemCount" | "itemPreview">): string {
  if (order.clientName) return order.clientName;
  if (order.itemPreview.length > 0) return order.itemPreview.slice(0, ORDER_ITEM_PREVIEW_LIMIT).join(", ");
  return `${order.itemCount} ite${order.itemCount === 1 ? "m" : "ns"}`;
}

export function orderItemsPreview(order: Pick<OrderSummary, "itemCount" | "itemPreview">): string {
  const itemsLabel = `${order.itemCount} ite${order.itemCount === 1 ? "m" : "ns"}`;
  if (order.itemPreview.length === 0) return itemsLabel;
  const shown = order.itemPreview.slice(0, ORDER_ITEM_PREVIEW_LIMIT);
  const remaining = Math.max(0, order.itemCount - shown.length);
  return `${itemsLabel} · ${shown.join(" · ")}${remaining > 0 ? ` +${remaining}` : ""}`;
}

/** yyyy-MM-dd (data pura, sem hora) → dd/MM/yyyy. Não faz conversão de fuso. */
export function formatDeliveryDate(date?: string | null): string | undefined {
  if (!date) return undefined;
  const [y, m, d] = date.split("-");
  return y && m && d ? `${d}/${m}/${y}` : undefined;
}

/** Rótulo de entrega: data (dd/MM/yyyy) e janela, quando houver. "—" se não houver nenhuma. */
export function formatDeliveryLabel(
  order: Pick<OrderSummary, "deliveryDate" | "deliveryWindow">,
): string {
  const date = formatDeliveryDate(order.deliveryDate);
  const window = order.deliveryWindow?.trim() || undefined;
  // Alguns pedidos antigos já trazem a data dentro da janela — não duplica.
  if (date && window && window.includes(date)) return window;
  const parts = [date, window].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

const BUSINESS_TZ = "America/Sao_Paulo";

export function toBusinessDate(iso?: string | null): string | undefined {
  if (!iso) return undefined;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: BUSINESS_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const y = parts.find((p) => p.type === "year")?.value ?? "";
  const m = parts.find((p) => p.type === "month")?.value ?? "";
  const d = parts.find((p) => p.type === "day")?.value ?? "";
  return `${y}-${m}-${d}`;
}

export function todayBusinessDate(): string {
  return toBusinessDate(new Date().toISOString()) ?? "";
}

/** Próximo dia útil de calendário (hoje + N dias) no fuso de negócio, em ISO yyyy-MM-dd. */
export function addBusinessDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return isoDate;
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Amanhã no fuso de negócio (hoje + 1), em ISO yyyy-MM-dd. */
export function tomorrowBusinessDate(): string {
  return addBusinessDays(todayBusinessDate(), 1);
}

export function formatBusinessDateShort(iso?: string | null): string | undefined {
  const date = toBusinessDate(iso);
  if (!date) return undefined;
  const [y, m, d] = date.split("-");
  return `${d}/${m}/${y}`;
}

export function formatBusinessDateTimeShort(iso?: string | null): string | undefined {
  if (!iso) return undefined;
  try {
    const parts = new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(new Date(iso));
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get("minute")}`;
  } catch {
    return undefined;
  }
}

export const STATUS_DATE_LABEL: Record<OrderStatus, string> = {
  CRIADO: "Criado em",
  EM_COMPRA: "Em compra",
  EM_PRODUCAO: "Em produção",
  EM_EXPEDICAO: "Em expedição",
  EM_ROTA: "Saiu p/ entrega em",
  ENTREGUE: "Entregue em",
  CANCELADO: "Cancelado em",
};

export function orderStatusDate(
  order: Pick<
    OrderSummary,
    | "createdAt"
    | "purchaseStartedAt"
    | "productionScheduledAt"
    | "expeditionScheduledAt"
    | "expeditionReleasedAt"
    | "routeStartedAt"
    | "deliveredAt"
    | "cancelledAt"
  >,
  status: OrderStatus,
): string | undefined {
  switch (status) {
    case "CRIADO":
      return order.createdAt;
    case "EM_COMPRA":
      return order.purchaseStartedAt;
    case "EM_PRODUCAO":
      return order.productionScheduledAt;
    case "EM_EXPEDICAO":
      return order.expeditionScheduledAt;
    case "EM_ROTA":
      return order.expeditionReleasedAt ?? order.routeStartedAt;
    case "ENTREGUE":
      return order.deliveredAt;
    case "CANCELADO":
      return order.cancelledAt;
  }
}

const STATUS_RANK: Record<OrderStatus, number> = {
  CRIADO: 0,
  EM_COMPRA: 1,
  EM_PRODUCAO: 2,
  EM_EXPEDICAO: 3,
  EM_ROTA: 4,
  ENTREGUE: 5,
  CANCELADO: -1,
};

export function bucketOrderForDate(order: OrderSummary, businessDate: string): OrderStatus | null {
  return bucketOrderForDateRange(order, businessDate, businessDate);
}

export function bucketOrderForDateRange(
  order: OrderSummary,
  fromBusinessDate: string,
  toBusinessDateStr: string,
): OrderStatus | null {
  const inRange = (iso?: string | null) => {
    const d = toBusinessDate(iso);
    if (!d) return false;
    return d >= fromBusinessDate && d <= toBusinessDateStr;
  };
  if (order.status === "CANCELADO") {
    return inRange(order.cancelledAt) ? "CANCELADO" : null;
  }
  const currentRank = STATUS_RANK[order.status];
  let best: OrderStatus | null = null;
  let bestRank = -1;
  const candidates: OrderStatus[] = ["CRIADO", "EM_COMPRA", "EM_PRODUCAO", "EM_EXPEDICAO", "EM_ROTA", "ENTREGUE"];
  for (const status of candidates) {
    if (STATUS_RANK[status] > currentRank) continue;
    if (!inRange(orderStatusDate(order, status))) continue;
    if (STATUS_RANK[status] > bestRank) {
      best = status;
      bestRank = STATUS_RANK[status];
    }
  }
  return best;
}

/**
 * Tipos de data que o filtro do Dashboard de Operações pode considerar. Cada tipo aponta para um campo
 * distinto do pedido — NÃO confundir com o status/coluna do Kanban:
 * - `DELIVERY_SCHEDULED` é a data de entrega *planejada* (`deliveryDate`), independente do status atual;
 * - os demais são o carimbo de *quando o pedido entrou na etapa* (execução), espelhando os rótulos do
 *   card. `PRODUCTION`/`EXPEDITION` referem-se à entrada na etapa (gravados na transição), não a um
 *   agendamento futuro (o dia agendado de produção é o campo separado `scheduledProductionDate`).
 */
export type OrderDateType =
  | "DELIVERY_SCHEDULED"
  | "CREATED"
  | "PURCHASE"
  | "PRODUCTION"
  | "EXPEDITION"
  | "RELEASED"
  | "DELIVERED";

/** Ordem canônica de exibição dos tipos no seletor e no resumo. */
export const ORDER_DATE_TYPE_ORDER: OrderDateType[] = [
  "DELIVERY_SCHEDULED",
  "CREATED",
  "PURCHASE",
  "PRODUCTION",
  "EXPEDITION",
  "RELEASED",
  "DELIVERED",
];

export const ORDER_DATE_TYPE_LABELS: Record<OrderDateType, string> = {
  DELIVERY_SCHEDULED: "Entrega agendada",
  CREATED: "Criado",
  PURCHASE: "Em compra",
  PRODUCTION: "Em produção",
  EXPEDITION: "Em expedição",
  RELEASED: "Saiu p/ entrega",
  DELIVERED: "Entregue",
};

/** Seleção inicial quando o filtro por data (Dia/Período) é ativado: só a entrega agendada. */
export const DEFAULT_ORDER_DATE_TYPES: OrderDateType[] = ["DELIVERY_SCHEDULED"];

/**
 * Extrai o dia de calendário (yyyy-MM-dd) de um campo que já é data pura, SEM aplicar fuso — `new Date`
 * em "2026-10-30" assume meia-noite UTC e, convertido para Brasília, deslocaria para o dia anterior.
 * Aceita tanto "2026-10-30" quanto um ISO com hora (usa só a parte da data, sem conversão).
 */
function calendarDay(value?: string | null): string | undefined {
  if (!value) return undefined;
  const day = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : undefined;
}

/**
 * Dia de calendário (yyyy-MM-dd) que o tipo de data representa para este pedido, ou `undefined` quando o
 * campo está ausente. Data pura (entrega agendada) é comparada direto; carimbos com hora são reduzidos
 * ao dia em Brasília. Nunca cai em outro campo quando o próprio está vazio.
 */
export function orderDateTypeDay(order: OrderSummary, type: OrderDateType): string | undefined {
  switch (type) {
    case "DELIVERY_SCHEDULED":
      return calendarDay(order.deliveryDate);
    case "CREATED":
      return toBusinessDate(order.createdAt);
    case "PURCHASE":
      return toBusinessDate(order.purchaseStartedAt);
    case "PRODUCTION":
      return toBusinessDate(order.productionScheduledAt);
    case "EXPEDITION":
      return toBusinessDate(order.expeditionScheduledAt);
    case "RELEASED":
      return toBusinessDate(order.expeditionReleasedAt ?? order.routeStartedAt);
    case "DELIVERED":
      return toBusinessDate(order.deliveredAt);
  }
}

/**
 * Filtro por tipo de data do Dashboard de Operações (apenas visibilidade — não altera a coluna/status do
 * pedido). Combina os tipos selecionados por OU: basta que a data de UM dos tipos caia na janela
 * [from, to] (inclusive, em dia de calendário). Data ausente não corresponde. Sem tipo selecionado,
 * nada corresponde (o chamador garante pelo menos um). O intervalo é normalizado caso venha invertido.
 */
export function orderMatchesDateFilter(
  order: OrderSummary,
  fromBusinessDate: string,
  toBusinessDateStr: string,
  selectedTypes: readonly OrderDateType[],
): boolean {
  if (selectedTypes.length === 0) return false;
  const [lo, hi] =
    fromBusinessDate <= toBusinessDateStr
      ? [fromBusinessDate, toBusinessDateStr]
      : [toBusinessDateStr, fromBusinessDate];
  return selectedTypes.some((type) => {
    const day = orderDateTypeDay(order, type);
    if (!day) return false;
    return day >= lo && day <= hi;
  });
}
